import { IconButton } from '@mui/material';
import styles from 'common/_variables.module.scss';
import { Chart } from 'components/Chart/Chart';
import { LoadingBar } from 'components/loading-bar/loading-bar';
import { usePropertyContext } from 'context/propertyContext';
import { PricingStrategy } from 'graphql/gql-types';
import { labels } from 'locales/en.label';
import { cloneDeep } from 'lodash';
import { useRateLevelsForDate } from 'pages/home/hooks/use-rate-levels-for-date';
import type { StrategyRates } from 'pages/home/hooks/use-strategy-rates';
import {
	type TrendData,
	useTrendDataForDate,
} from 'pages/home/hooks/use-trend-data-for-date';
import { type ReactNode, type SyntheticEvent, useState } from 'react';
import {
	MdKeyboardArrowLeft,
	MdKeyboardArrowRight,
	MdLock,
} from 'react-icons/md';
import Carousel from 'react-material-ui-carousel';
import { buildTooltip, trendChartConfig } from '../trend-widget/config';

// The carousel only measures a slide's height when it becomes active,
// so insight slides use a fixed height to avoid clipping once data loads.
const INSIGHT_SLIDE_HEIGHT = 150;

// Header (26px) + 58px tiles + a small gap above the indicator row.
// Keep in sync with .strategy-tile in date-feed.scss.
const STRATEGY_SLIDE_HEIGHT = 88;

const InsightSlide = ({
	title,
	aside,
	height = INSIGHT_SLIDE_HEIGHT,
	children,
}: {
	title: string;
	aside?: ReactNode;
	height?: number;
	children: ReactNode;
}) => (
	// Taps inside insight slides (e.g. chart tooltips) shouldn't toggle card selection
	<div
		className='insight-slide'
		style={{ height }}
		onClick={(e) => e.stopPropagation()}
		onKeyDown={(e) => e.stopPropagation()}
	>
		<div className='insight-header'>
			<p className='label insight-title'>{title}</p>
			{aside}
		</div>
		{children}
	</div>
);

const isClosed = (status: string | undefined) =>
	status?.toUpperCase().startsWith('CLOSE') ?? false;

const OtbSlide = ({ stayDate }: { stayDate: string }) => {
	const { rateLevels, total, loading } = useRateLevelsForDate(stayDate);
	const { otb_title, rooms, closed } = labels.home.day_card_insights;

	const rows = (rateLevels ?? []).map(({ level, otb, liveOtb, status }) => ({
		level,
		value: liveOtb ?? otb ?? 0,
		closed: isClosed(status),
	}));
	// Bars share one scale so lengths are comparable across levels
	const max = Math.max(1, ...rows.map(({ value }) => value));

	return (
		<InsightSlide
			title={otb_title}
			aside={
				total !== undefined && (
					<span className='insight-aside'>
						<strong>{total}</strong> {rooms}
					</span>
				)
			}
		>
			<LoadingBar loading={loading}>
				{rateLevels && total !== undefined ? (
					<ul className='otb-bars'>
						{rows.map(({ level, value, closed: isLevelClosed }) => (
							<li
								key={level}
								className={isLevelClosed ? 'otb-bar closed' : 'otb-bar'}
								aria-label={`${level}: ${value} ${rooms}${
									isLevelClosed ? `, ${closed}` : ''
								}`}
							>
								<span className='otb-bar__level'>{level}</span>
								<span className='otb-bar__track'>
									{value > 0 && (
										<span
											className='otb-bar__fill'
											style={{ width: `${(value / max) * 100}%` }}
										/>
									)}
								</span>
								<span className='otb-bar__value'>
									{isLevelClosed && <MdLock title={closed} />}
									{value}
								</span>
							</li>
						))}
					</ul>
				) : loading ? null : (
					// Only after loading; LoadingBar keeps children visible under its overlay
					<span className='no-data'>{labels.home.no_data}</span>
				)}
			</LoadingBar>
		</InsightSlide>
	);
};

// Height left for the chart after the slide header
const TREND_CHART_HEIGHT = INSIGHT_SLIDE_HEIGHT - 26;

const buildCompactTrendOptions = (
	{ bwRates, marketRates, recRates }: TrendData,
	currencySymbol: string,
) => {
	const config = cloneDeep(trendChartConfig);
	const axisLabelStyle = { color: styles.mainGrey, fontSize: '0.625rem' };

	config.chart = {
		...config.chart,
		type: 'line',
		height: TREND_CHART_HEIGHT,
		spacing: [4, 4, 0, 0],
		backgroundColor: 'transparent',
	};
	config.title = { text: undefined };
	config.subtitle = { text: undefined };
	config.legend = {
		...config.legend,
		width: undefined,
		itemWidth: undefined,
		maxHeight: undefined,
		margin: 4,
		padding: 0,
		itemDistance: 12,
		symbolWidth: 10,
		itemStyle: { ...config.legend.itemStyle, fontSize: '0.625rem' },
	};
	config.plotOptions = {
		line: { marker: { enabled: true, radius: 2 }, lineWidth: 2 },
	};
	config.xAxis.labels.style = axisLabelStyle;
	// Rates only: pickup is shown in the slide header to avoid a second y-axis.
	// Colors match the desktop trend widget so each series keeps its identity.
	config.series = [
		{ name: 'BWH Rate', color: styles.graphBrown, data: bwRates },
		{ name: 'Market Rate', color: styles.graphLightBlue, data: marketRates },
		{ name: 'Rec Rate', color: styles.graphOrange, data: recRates },
	];
	config.yAxis = {
		title: { text: undefined },
		labels: { style: axisLabelStyle, format: `${currencySymbol}{value}` },
		tickAmount: 3,
		gridLineColor: 'rgba(196, 196, 196, 0.2)',
	};
	config.tooltip = buildTooltip(currencySymbol);

	return config;
};

const formatPickup = (pickupData: TrendData['pickupData']) => {
	const total = pickupData.reduce<number>(
		(sum, value) => sum + (value ?? 0),
		0,
	);
	return total > 0 ? `+${total}` : `${total}`;
};

const TrendSlide = ({ stayDate }: { stayDate: string }) => {
	const { currencySymbol } = usePropertyContext();
	const { data, loading } = useTrendDataForDate(stayDate);
	const { trend_title, pickup } = labels.home.day_card_insights;

	return (
		<InsightSlide
			title={trend_title}
			aside={
				data && (
					<span className='insight-aside'>
						{pickup} <strong>{formatPickup(data.pickupData)}</strong>
					</span>
				)
			}
		>
			<LoadingBar loading={loading}>
				{data ? (
					<Chart options={buildCompactTrendOptions(data, currencySymbol)} />
				) : loading ? null : (
					<span className='no-data'>{labels.home.no_data}</span>
				)}
			</LoadingBar>
		</InsightSlide>
	);
};

const STRATEGIES = [
	{ strategy: PricingStrategy.conservative, field: 'conservative_rate' },
	{ strategy: PricingStrategy.neutral, field: 'neutral_rate' },
	{ strategy: PricingStrategy.aggressive, field: 'aggressive_rate' },
] as const;

// Rates are compared and applied at cent precision, matching the override field
const toCents = (rate: number) => Math.round(rate * 100) / 100;
const isSameRate = (a: number, b: number | undefined) =>
	b !== undefined && b !== null && toCents(a) === toCents(b);

const formatDiffFromRec = (
	rate: number,
	recRate: number | undefined,
	currencySymbol: string,
) => {
	if (recRate === undefined || recRate === null) return '';
	const diff = toCents(rate) - toCents(recRate);
	if (Math.abs(diff) < 0.005) return labels.home.day_card_insights.matches_rec;
	const sign = diff > 0 ? '+' : '-';
	return `${sign}${currencySymbol}${Math.abs(diff).toFixed(2)}`;
};

export type StrategySlideProps = {
	rates: StrategyRates | undefined;
	recRate: number | undefined;
	overrideRate: number | undefined;
	/** Read-only users and in-flight uploads can't change the rate */
	disabled: boolean;
	onApply: (rate: number) => void;
};

const StrategySlide = ({
	rates,
	recRate,
	overrideRate,
	disabled,
	onApply,
}: StrategySlideProps) => {
	const { currencySymbol } = usePropertyContext();
	const { strategy_title, strategies, current, apply } =
		labels.home.day_card_insights;

	const tiles = STRATEGIES.map(({ strategy, field }) => {
		const rate = rates?.[field];
		return { strategy, rate: typeof rate === 'number' ? rate : undefined };
	});
	const hasRates = tiles.some(({ rate }) => rate !== undefined);

	return (
		<InsightSlide title={strategy_title} height={STRATEGY_SLIDE_HEIGHT}>
			{hasRates ? (
				<div className='strategy-tiles'>
					{tiles.map(({ strategy, rate }) => {
						const name = strategies[strategy];
						const isCurrent =
							rate !== undefined && isSameRate(rate, overrideRate);
						const formattedRate =
							rate === undefined ? '--' : `${currencySymbol}${rate.toFixed(2)}`;
						return (
							<button
								key={strategy}
								type='button'
								className={`strategy-tile strategy-tile--${strategy}${
									isCurrent ? ' current' : ''
								}`}
								aria-pressed={isCurrent}
								aria-label={`${apply} ${name} ${formattedRate}`}
								disabled={disabled || rate === undefined}
								onClick={() => {
									// Re-applying the current rate would only fire a redundant mutation
									if (rate === undefined || isCurrent) return;
									onApply(toCents(rate));
								}}
							>
								<span className='strategy-tile__name'>{name}</span>
								<span className='strategy-tile__rate'>{formattedRate}</span>
								<span className='strategy-tile__meta'>
									{isCurrent
										? current
										: rate !== undefined &&
											formatDiffFromRec(rate, recRate, currencySymbol)}
								</span>
							</button>
						);
					})}
				</div>
			) : (
				<span className='no-data'>{labels.home.no_data}</span>
			)}
		</InsightSlide>
	);
};

type DayCardCarouselProps = {
	stayDate: string;
	strategy: StrategySlideProps;
	/**
	 * Desktop/tablet: only the summary and strategy slides, since OTB and
	 * trend already have their own dashboard widgets.
	 */
	compact?: boolean;
	children: ReactNode;
};

const NAV_SELECTOR = '.day-card-carousel__indicators, .day-card-carousel__nav';

/**
 * Carousel for a day card. The first slide is the regular card summary;
 * swiping reveals pricing strategies and, on mobile, metrics that only fit
 * on the desktop dashboard. Insight slides with their own queries mount on
 * first view so each card doesn't fire extra requests.
 */
export const DayCardCarousel = ({
	stayDate,
	strategy,
	compact = false,
	children,
}: DayCardCarouselProps) => {
	const [visited, setVisited] = useState<Set<number>>(() => new Set([0]));

	// Strategy rates are fetched once for the whole feed, so this slide is cheap to mount eagerly
	const slides = [
		<div key='summary'>{children}</div>,
		<div key='strategy'>
			<StrategySlide {...strategy} />
		</div>,
	];
	if (!compact) {
		slides.push(
			<div key='otb'>
				{visited.has(2) ? (
					<OtbSlide stayDate={stayDate} />
				) : (
					<InsightSlide title={labels.home.day_card_insights.otb_title}>
						{null}
					</InsightSlide>
				)}
			</div>,
			<div key='trend'>
				{visited.has(3) ? (
					<TrendSlide stayDate={stayDate} />
				) : (
					<InsightSlide title={labels.home.day_card_insights.trend_title}>
						{null}
					</InsightSlide>
				)}
			</div>,
		);
	}

	// Indicator and arrow taps shouldn't toggle card selection
	const stopNavPropagation = (e: SyntheticEvent) => {
		if ((e.target as HTMLElement).closest(NAV_SELECTOR)) {
			e.stopPropagation();
		}
	};

	return (
		<div onClick={stopNavPropagation} onKeyDown={stopNavPropagation}>
			<Carousel
				// Remount when the layout changes so the active index stays in range
				key={compact ? 'compact' : 'full'}
				className={`day-card-carousel${
					compact ? ' day-card-carousel--compact' : ''
				}`}
				autoPlay={false}
				animation='slide'
				swipe
				cycleNavigation={false}
				// Arrows on every device: easier to hit than the dots, and the only
				// affordance on desktop where there's no touch swipe
				navButtonsAlwaysVisible
				navButtonsWrapperProps={{ className: 'day-card-carousel__nav' }}
				NavButton={({ onClick, next }) => (
					<IconButton
						size='small'
						className='day-card-carousel__nav-btn'
						aria-label={
							next
								? labels.home.day_card_insights.next_slide
								: labels.home.day_card_insights.previous_slide
						}
						// The carousel's handler also stops propagation to the card
						onClick={(e) => onClick(e)}
					>
						{next ? <MdKeyboardArrowRight /> : <MdKeyboardArrowLeft />}
					</IconButton>
				)}
				indicatorContainerProps={{
					className: 'day-card-carousel__indicators',
				}}
				onChange={(now) => {
					if (now === undefined || visited.has(now)) return;
					setVisited((prev) => new Set(prev).add(now));
				}}
			>
				{slides}
			</Carousel>
		</div>
	);
};
